/**
 * Página de visualización de permisos del sistema
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState } from 'react';
import {
  FiSearch, FiX, FiAlertTriangle, FiShield,
  FiSettings, FiLayers
} from 'react-icons/fi';
import { usePermissions } from '../../hooks';
import { Loading } from '../../components/UI/Loading';

const Permissions = () => {
  // Estados locales
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState('');

  // Hooks para datos
  const {
    permissions,
    permissionModules,
    loading,
    error,
    setError
  } = usePermissions();

  const totalPermissions = permissions?.length || 0;

  if (loading && (!permissions || permissions.length === 0)) {
    return <Loading message="Cargando permisos..." />;
  }

  return (
    <div className="permissions-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Permisos del Sistema</h1>
          <p className="page-description">
            Listado de permisos disponibles en el sistema. Los permisos son definidos por cada módulo y se asignan a roles.
          </p>
        </div>
      </div>

      {/* Estadísticas rápidas */}
      <div className="permissions-stats" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        <div className="stat-card" style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div className="stat-icon" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FiShield size={24} />
          </div>
          <div className="stat-info">
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{totalPermissions}</div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Total Permisos</div>
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div className="stat-icon" style={{
            background: 'linear-gradient(135deg, #7B2D8E 0%, #5a1f6a 100%)',
            color: 'white',
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FiLayers size={24} />
          </div>
          <div className="stat-info">
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{permissionModules?.length || 0}</div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Módulos</div>
          </div>
        </div>
      </div>

      {/* Filtros mejorados */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL PERMISO"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="menu-item menu-item--secondary" style={{
            flex: '0 0 auto',
            minWidth: '200px',
            padding: '0 20px'
          }}>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="search-input"
              style={{
                width: '100%',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              <option value="">TODOS LOS MÓDULOS</option>
              {permissionModules && permissionModules.map(module => (
                <option key={module} value={module}>
                  {module.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <button
            className="menu-item menu-item--warning"
            onClick={() => {
              setSearchTerm('');
              setSelectedModule('');
            }}
            style={{
              flex: '0 0 auto',
              minWidth: '140px',
              gap: '8px'
            }}
          >
            <FiX />
            LIMPIAR
          </button>
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="alert alert-error">
          <FiAlertTriangle />
          {error}
          <button 
            className="btn btn-link"
            onClick={() => setError(null)}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Listado simple de permisos */}
      <div className="permissions-container">
        {(!permissions || permissions.length === 0) && !loading ? (
          <div className="empty-state">
            <FiShield />
            <h3>No se encontraron permisos</h3>
            <p>
              {searchTerm || selectedModule
                ? 'No hay permisos que coincidan con los filtros aplicados.'
                : 'No hay permisos configurados en el sistema.'
              }
            </p>
          </div>
        ) : (
          <div className="permissions-table-container" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
          }}>
            <table className="permissions-table" style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid #e5e7eb',
                  background: '#f9fafb'
                }}>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Nombre del Permiso</th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Código</th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Módulo</th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Descripción</th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    width: '100px'
                  }}>Grupo</th>
                </tr>
              </thead>
              <tbody>
                {permissions && permissions
                  .filter(permission => {
                    if (selectedModule && permission.module !== selectedModule) return false;
                    if (!searchTerm) return true;

                    const searchLower = searchTerm.toLowerCase();
                    return (
                      (permission.display_name || permission.name).toLowerCase().includes(searchLower) ||
                      (permission.description || '').toLowerCase().includes(searchLower) ||
                      permission.name.toLowerCase().includes(searchLower) ||
                      (permission.module || '').toLowerCase().includes(searchLower)
                    );
                  })
                  .map((permission, index) => (
                  <tr key={permission.id} style={{
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background 0.2s ease',
                    background: index % 2 === 0 ? 'white' : '#fafbfc'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafbfc'}
                  >
                    <td style={{
                      padding: '14px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#111827'
                    }}>
                      {permission.display_name || permission.name}
                    </td>
                    <td style={{
                      padding: '14px 16px',
                      fontSize: '13px',
                      color: '#6b7280',
                      fontFamily: 'monospace'
                    }}>
                      <code style={{
                        background: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {permission.name}
                      </code>
                    </td>
                    <td style={{
                      padding: '14px 16px',
                      fontSize: '13px',
                      color: '#374151'
                    }}>
                      <span style={{
                        background: '#7B2D8E',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {permission.module || 'GENERAL'}
                      </span>
                    </td>
                    <td style={{
                      padding: '14px 16px',
                      fontSize: '13px',
                      color: '#6b7280',
                      maxWidth: '300px'
                    }}>
                      {permission.description || 'Sin descripción'}
                    </td>
                    <td style={{
                      padding: '14px 16px',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        background: '#f3f4f6',
                        color: '#4b5563',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {permission.group || 'GENERAL'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <Loading message="Cargando permisos..." />
        </div>
      )}
    </div>
  );
};

export default Permissions;
